import {
  findFutureCostDetail,
  getNettedVolume,
  getRedeliveryRemainder,
  hasFutureCostDetails,
  splitByPeriod,
} from './netting';
import type { Offer } from '../../types';

export interface PricedVolume {
  /** kWh the amount is paid over. */
  volume: number;
  /** Per-kWh rate excluding VAT, when DC provides one. */
  rate?: number;
  /** Yearly amount excluding VAT, exactly as DC reports it (a refund is a negative amount). */
  amount?: number;
}

export interface NettingFigures {
  current: {
    netted: number;
    refund: PricedVolume;
  };
  future: {
    refund: PricedVolume;
  };
}

/**
 * Selects the netting inputs off a mapped SME offer and composes them into per-period figures. Volumes are
 * derived with the pure functions in `./netting`; money is never recomputed, only read from DC — excluding
 * VAT, as all B2B prices are. B2B has no redelivery costs (terugleverkosten), so only the refund is read.
 *
 * The period split mirrors the consumer funnel: the current-period volume is the total redelivery minus the
 * volume DC attributes to the future period. The netted volume is DC's `estimatedUsages.nettedUsage`, and what
 * is left of the current-period redelivery after netting is paid the current redelivery tariff.
 *
 * Returns `undefined` when there is nothing to disclose: no future cost breakdown, or no redelivery at all.
 */
export const getNettingFigures = (offer?: Offer): NettingFigures | undefined => {
  const { futureCostDetails, productFutureRate, estimatedUsages, usages, costDetails } = offer ?? {};

  if (!hasFutureCostDetails(futureCostDetails)) {
    return undefined;
  }

  const totalRedelivery = (usages?.electricityRedeliveryNormal ?? 0) + (usages?.electricityRedeliveryLow ?? 0);

  if (totalRedelivery <= 0) {
    return undefined;
  }

  const futureRefund = findFutureCostDetail(futureCostDetails, 'electricityRedeliveryRefund');
  const redelivery = splitByPeriod(totalRedelivery, futureRefund?.usage);

  // DC nets against consumption itself and reports the result; `usage` / `futureUsage` are not a total and
  // its future share, so the netted volume is never derived from them. It is capped at the current-period
  // redelivery, since more than was redelivered cannot be netted.
  const netted = getNettedVolume(redelivery.current, estimatedUsages?.nettedUsage);

  return {
    current: {
      netted,
      refund: {
        volume: getRedeliveryRemainder(redelivery.current, netted),
        rate: costDetails?.electricityCostDetails?.redeliverytariff?.price?.vatExcluded,
        amount: costDetails?.electricityTotal?.electricityRedeliveryRefund?.vatExcluded,
      },
    },
    future: {
      refund: {
        volume: redelivery.future,
        rate: productFutureRate?.electricityRedeliveryRefund?.vatExcluded,
        amount: futureRefund?.costs?.vatExcluded,
      },
    },
  };
};

/** A priced row is worth showing only with a volume to pay over and a non-zero amount DC reported for it. */
export const isPricedVolumeVisible = ({ volume, amount }: PricedVolume): boolean =>
  volume > 0 && amount !== undefined && amount !== 0;

/**
 * The single rule for whether the disclosure exists: both periods must have something to show. Used by the
 * builder and by every caller that has to decide between the disclosure and what it replaces, so they can
 * never disagree and leave the customer with neither.
 */
export const hasNettingDisclosure = (figures?: NettingFigures): figures is NettingFigures =>
  !!figures &&
  (figures.current.netted > 0 || isPricedVolumeVisible(figures.current.refund)) &&
  isPricedVolumeVisible(figures.future.refund);

export const isNettingDisclosureAvailable = (offer?: Offer): boolean => hasNettingDisclosure(getNettingFigures(offer));
